/**
 * "Save as chart" - the one way a view becomes a catalog entry.
 *
 * Everything on this dialog is a decision the saver has to make once and nobody
 * can make for them afterwards, which is why there are only four fields. The
 * Ships and time are separate pins. Each unpinned dimension follows the viewer;
 * the default keeps both dimensions from the view being saved.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { Atom } from '../../state/store.ts';
import { StoreController } from '../../state/controller.ts';
import { activeSection, activeTab, catalogAvailable, plottedShipIds } from '../../state/store.ts';
import { isPlotEnabled } from '../../state/plot-state.ts';
import { shipLabel } from '../../utils/ships.ts';
import { refreshChart, saveCurrentAsChart } from './catalog-state.ts';
import { captureAndStore } from './thumbnail.ts';
import { periodComparison, resolveComparisonPeriods } from '../../state/period-comparison.ts';

/** Open/closed, as an atom so the chart-header Save action can open it. */
export const savingChart = new Atom<boolean>(false);

export function openSaveChartDialog(): void {
  savingChart.set(true);
}

@customElement('save-chart-dialog')
export class SaveChartDialog extends LightElement {
  private store = new StoreController(this, [savingChart, periodComparison]);

  // `chartTitle`, not `title`: HTMLElement already has a `title`, and shadowing
  // it breaks the element's typing (the chart builder names its field the same).
  @state() private chartTitle = '';
  @state() private description = '';
  @state() private tags = '';
  /**
   * Not pinned by default (since 2026-10-19).
   *
   * A saved chart follows the ships and time of whoever opens it, so it fits
   * any dashboard. Keeping the exact ships or window is an explicit choice.
   * Charts saved before keep the pins they were saved with.
   */
  @state() private pinShip = false;
  @state() private pinTime = false;
  @state() private busy = false;

  /**
   * `S` opens this dialog from anywhere in Explore.
   *
   * The listener lives here because this element is always mounted, so there is
   * no separate registry to keep in step with what the shortcut opens. It is
   * deliberately narrow: no modifiers (⌘S belongs to the browser), never while
   * typing, never while another dialog is open, and only when there is
   * something plottable to save into a catalog that exists. A shortcut that
   * fires in a text field is worse than no shortcut.
   */
  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 's' && e.key !== 'S') return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (savingChart.value) return;
    // `Element`, not `HTMLElement`: a key event can be targeted at the document
    // itself, which has neither `tagName` nor `closest` - reaching for them
    // there throws inside the listener and the shortcut silently does nothing.
    const target = e.target;
    if (target instanceof HTMLElement) {
      if (
        target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
        target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
    }
    if (document.querySelector('.share-dialog, .cbuilder')) return;
    if (activeSection.value !== 'explore' || activeTab.value !== 'plot') return;
    if (!catalogAvailable.value || !isPlotEnabled()) return;
    e.preventDefault();
    savingChart.set(true);
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKeyDown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKeyDown);
  }

  private close(): void {
    savingChart.set(false);
    this.chartTitle = '';
    this.description = '';
    this.tags = '';
    this.pinShip = false;
    this.pinTime = false;
  }

  private async save(): Promise<void> {
    const title = this.chartTitle.trim();
    if (!title || this.busy) return;
    this.busy = true;
    const saved = await saveCurrentAsChart({
      title,
      description: this.description.trim(),
      tags: this.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      pinShip: this.pinShip,
      pinTime: this.pinTime,
    });
    this.busy = false;
    if (saved) {
      this.close();
      // After the close: the chart is saved either way, and a preview that
      // cannot be captured (nothing plotted, a tainted canvas) is a card with a
      // glyph on it, not a failure worth holding the dialog open for.
      void captureAndStore(saved.id, saved.version).then((stored) => {
        if (stored) void refreshChart(saved.id);
      });
    }
  }

  override render(): TemplateResult | typeof nothing {
    if (!savingChart.value) return nothing;
    // The name, not the code: `A-BL` is an identifier, `AIDAblu` is a ship.
    const ships = plottedShipIds();
    const ship = ships.length > 1
      ? `${shipLabel(ships[0]!)} and ${ships.length - 1} other ${ships.length === 2 ? 'ship' : 'ships'}`
      : ships[0] ? shipLabel(ships[0]) : null;
    const comparing = !!periodComparison.value;
    const periodCount = comparing ? resolveComparisonPeriods().length : 0;
    return html`
      <div class="share-scrim" @click=${() => this.close()}></div>
      <div class="share-dialog" role="dialog" aria-modal="true" aria-label="Save as chart" data-tour="save-dialog">
        <header class="share-head">
          <h2>Save as chart</h2>
          <button class="catalog-btn" @click=${() => this.close()}>Cancel</button>
        </header>

        <label class="save-field">
          Title
          <input
            class="catalog-search"
            .value=${this.chartTitle}
            placeholder="Propulsion power vs speed"
            autofocus
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter') void this.save();
            }}
            @input=${(e: Event) => {
              this.chartTitle = (e.target as HTMLInputElement).value;
            }}
          />
        </label>

        <label class="save-field">
          Description <span class="save-optional">optional</span>
          <input
            class="catalog-search"
            .value=${this.description}
            placeholder="What this is for"
            @input=${(e: Event) => {
              this.description = (e.target as HTMLInputElement).value;
            }}
          />
        </label>

        <label class="save-field">
          Tags <span class="save-optional">comma separated</span>
          <input
            class="catalog-search"
            .value=${this.tags}
            placeholder="fuel, weekly review"
            @input=${(e: Event) => {
              this.tags = (e.target as HTMLInputElement).value;
            }}
          />
        </label>

        <div class="save-pin">
          <span class="save-pin-heading">Context</span>
          <button class="dset-toggle ${this.pinShip ? 'is-on' : ''}" type="button"
            role="switch" aria-checked=${this.pinShip ? 'true' : 'false'}
            @click=${() => { this.pinShip = !this.pinShip; }}>
            <span class="dset-toggle-text">
              <strong>Pin ships</strong>
              <span>${this.pinShip ? ship ?? 'Keep the ships shown now' : 'Follow the viewer’s ships'}</span>
            </span>
            <span class="sd-switch" aria-hidden="true"></span>
          </button>
          <button class="dset-toggle ${this.pinTime ? 'is-on' : ''}" type="button"
            role="switch" aria-checked=${this.pinTime ? 'true' : 'false'}
            @click=${() => { this.pinTime = !this.pinTime; }}>
            <span class="dset-toggle-text">
              <strong>${comparing ? 'Pin periods' : 'Pin time range'}</strong>
              <span>${this.pinTime
                ? comparing ? `Keep ${periodCount} fixed periods` : 'Keep the window shown now'
                : 'Follow the viewer’s time range'}</span>
            </span>
            <span class="sd-switch" aria-hidden="true"></span>
          </button>
        </div>

        <div class="save-actions">
          <button
            class="catalog-cta"
            ?disabled=${!this.chartTitle.trim() || this.busy}
            @click=${() => void this.save()}
          >
            ${this.busy ? 'Saving…' : 'Save to my catalog'}
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'save-chart-dialog': SaveChartDialog;
  }
}
